import { Slot } from "@radix-ui/react-slot";

import * as React from "react";

import type { Icon } from "@/components/ui/Icons";
import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";

const disabledButtonsClasses = [
  "disabled:bg-button-disabled",
  "disabled:text-button-disabled",
  "disabled:border-none",
  "disabled:cursor-not-allowed",
];

const buttonVariants = cva(
  [
    "inline-flex",
    "shadow-none",
    "items-center",
    "justify-center",
    "whitespace-nowrap",
    "rounded-lg",
    "cursor-pointer",
    "relative",
    ...disabledButtonsClasses,
  ],
  {
    variants: {
      variant: {
        primary: [
          "text-button-primary",
          "bg-button-primary",
          "border",
          "border-link",
        ],
        secondary: [
          "text-button-secondary",
          "bg-button-secondary",
          "border",
          "border-link",
        ],
        link: [
          "text-button-link",
          "bg-transparent",
          "border-none",
          "!p-0",
          "cursor-pointer",
          "disabled:text-button-link-disabled",
          "disabled:cursor-not-allowed",
          "disabled:bg-transparent",
        ],
        warning: [
          "text-button-warning",
          "bg-button-warning",
          "border",
          "border-warning",
          ...disabledButtonsClasses,
        ],
        danger: [
          "text-button-primary",
          "bg-button-danger",
          "border",
          "border-error",
          ...disabledButtonsClasses,
        ],
        icon: [
          "bg-transparent",
          "disabled:bg-transparent",
          "disabled:text-gray-400",
        ],
        ghost: ["hover:bg-neutral-100, hover:text-neutral-900"],
      },
      size: {
        lg: ["p-3", "text-heading-md", "font-bold"],
        md: ["px-3", "py-2", "text-heading-sm", "font-bold"],
        sm: [
          "px-2",
          "py-1.5",
          "text-body-sm", // todo: using text-body-* elsewhere but here :/
          "font-bold",
        ],
        xs: ["px-1.5 py-1 text-body-xs font-bold"],
        icon: ["w-10", "h-10"],
        snatched: [],
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  leftIcon?: Icon;
  hideTextOnLoading?: boolean;
}

/**
 * A button component that can be used to render buttons in different sizes and variants
 * @param variant The variant of the button
 * @param size The size of the button
 * @param asChild https://www.radix-ui.com/primitives/docs/guides/composition
 * @returns A button component
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      type = "button",
      asChild = false,
      isLoading,
      hideTextOnLoading = true,
      children,
      disabled,
      leftIcon,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const LeftIconComp = leftIcon;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isLoading || disabled}
        type={type}
        {...props}
      >
        {isLoading && hideTextOnLoading && (
          <span className="h-4 w-4 absolute left-1/2 top-1/2 z-10 transform -translate-x-1/2 -translate-y-1/2">
            <LoaderCircle className="h-full w-full animate-spin" />
          </span>
        )}
        <span
          className={cn(
            "",
            isLoading && hideTextOnLoading && "opacity-0",
            (leftIcon || isLoading) &&
              "flex flex-row gap-1 items-start leading-[1.1rem]",
          )}
        >
          {!isLoading && LeftIconComp && <LeftIconComp className="h-4 w-4" />}
          {isLoading && !hideTextOnLoading && (
            <LoaderCircle className="h-4 w-4 animate-spin" />
          )}
          {children}
        </span>
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
